import type { ApacCandidateHeader } from '@seek/apac-candidate-header-footer';

import { metrics } from '@seek/metrics-js';
import { oauth } from '@seek/online-identity';

import { Box, ContentBlock, PageBlock } from 'braid-design-system';

import React, { type ComponentProps, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from 'src/reducers';
import { getUser, updateUserInfo } from 'src/reducers/user/actionCreators';
import { getLanguagePrefixedPath } from 'src/utils/getLanguagePrefixedPath';
import Footer from '../Footer/Footer';
import Header from '../Header/Header';

import { Settings } from '../Settings/Settings';
import { pathShouldNotRedirectToSignIn } from 'src/utils/pathShouldNotRedirectToSignIn';
import { useMelwaysInfo } from '@seek/melways-react';

export type ApacHeaderFooterAuthenticationStatus = ComponentProps<
  typeof ApacCandidateHeader
>['authenticationStatus'];

export const App = () => {
  const { language } = useMelwaysInfo();

  const firstName = useTypedSelector((state) => state.user.firstName);
  const lastName = useTypedSelector((state) => state.user.lastName);
  const email = useTypedSelector((state) => state.user.emailAddress);

  const country = useTypedSelector((store) => store.page.pageAnalytics.country);
  const brand = useTypedSelector((store) => store.page.pageAnalytics.brand);

  const authenticationStatus = useTypedSelector(
    (state) => state.user.authenticationStatus,
  );

  const dispatch = useDispatch();

  // Similar to componentDidMount and componentDidUpdate:
  useEffect(() => {
    const getUserApi = async () => {
      if (authenticationStatus === 'pending') {
        // Update the document title using the browser API
        const userData: any = await dispatch(getUser(language, country, brand));
        const userDetails = {
          loginId: userData?.id,
          isLoggedIn: true,
          loginGuid: userData?.emailCorrelationId,
          userLanguage: language,
        };
        dispatch(
          updateUserInfo(
            userDetails.loginId,
            userDetails.isLoggedIn,
            userDetails.loginGuid,
            userDetails.userLanguage,
          ),
        );
      }
    };
    getUserApi();
  });

  useEffect(() => {
    if (authenticationStatus === 'authenticated') {
      metrics.sendInitialLoadTime('settings');
    }
  }, [authenticationStatus]);

  if (authenticationStatus === 'unauthenticated') {
    const nextStepUrl = `${oauth.buildLoginUrl({
      location: {
        origin: '',
        pathname: getLanguagePrefixedPath(language, '/settings'),
        search: '',
      },
      language,
    })}`;
    if (
      typeof window !== 'undefined' &&
      !pathShouldNotRedirectToSignIn(window.location.pathname, language)
    ) {
      window.location.assign(nextStepUrl);
    }
  }

  return (
    <>
      <Header
        authenticationStatus={
          authenticationStatus as ApacHeaderFooterAuthenticationStatus
        }
        userProfile={{
          firstName,
          lastName,
          email,
        }}
      />
      <Box paddingY="xlarge">
        <PageBlock width="medium">
          <ContentBlock width="small">
            <Settings />
          </ContentBlock>
        </PageBlock>
      </Box>
      <Footer
        authenticationStatus={
          authenticationStatus as ApacHeaderFooterAuthenticationStatus
        }
      />
    </>
  );
};
